import { apiClient } from "../axiosInstance";
import { User, type IUser } from "../../models";
import {
	getLoginToken,
	NEW_USER_TOKEN,
	StandardAxiosConfig,
	REQUEST_CONFIG_NOAUTH,
	setJwtToken,
	setLoginToken,
	setRefreshToken,
	USER_NOT_FOUND,
} from "../common";
import { UserSettings } from "../../utilities/types";

export interface ImpersonatableUser {
	id: string;
	email: string;
	name: string;
	companyIds: string[];
}

export class UsersAPI {
	baseUrl: string;

	constructor() {
		this.baseUrl = `${import.meta.env.VITE_ROGO_API_URL}/auth`;
	}

	public async resetPassword(email: string) {
		const response = await apiClient.post(`${this.baseUrl}/reset-password`, { email }, REQUEST_CONFIG_NOAUTH);
		return response.data;
	}

	public async validateNewPasswordToken(token: string) {
		const response = await apiClient.post(`${this.baseUrl}/validate-token`, { token: token }, REQUEST_CONFIG_NOAUTH);
		return response.data.email;
	}

	public async updatePassword(email: string = "", password: string = "", token: string = "") {
		const params = new URLSearchParams();
		params.append("grant_type", "password");
		params.append("username", email);
		params.append("password", password);
		params.append("scope", "");
		params.append("client_id", "");
		params.append("client_secret", token);

		const response = await apiClient.post<{ access_token: string; token_type: string }>(
			`${this.baseUrl}/update-password`,
			params,
		);
		return response.status === 200;
	}

	public async register(email: string = "", password: string = "", firstName: string = "", lastName: string = "") {
		const params = {
			username: email,
			password: password,
			firstName: firstName,
			lastName: lastName,
		};
		const response = await apiClient.post<{ access_token: string; token_type: string }>(
			`${this.baseUrl}/register`,
			params,
		);

		const token = response.data.access_token;
		if (token == NEW_USER_TOKEN) {
			const user = {};
			return { ...user, token, refreshToken: "" };
		} else {
			const user = await this._login(token);

			return User.fromIUser(user, token);
		}
	}

	public async login(email: string, password: string) {
		const params = new URLSearchParams();
		params.append("grant_type", "password");
		params.append("username", email || "");
		params.append("password", password || "");
		params.append("scope", "");
		params.append("client_id", "");
		params.append("client_secret", "");

		const response = await apiClient.post<{ access_token: string; token_type: string }>(
			`${this.baseUrl}/login`,
			params,
		);
		const token = response.data.access_token;

		const user = await this._login(token);

		return User.fromIUser(user, token);
	}

	private async _login(token: string) {
		setLoginToken(token);
		const user = await this.me();
		if (!user?.username) {
			throw new Error(USER_NOT_FOUND);
		}
		setJwtToken(token);
		setRefreshToken("");
		this._storeProgagricaTokens(user);
		return user;
	}

	public async me() {
		const queryParams: string[] = [];
		queryParams.push(`use_cache=false`);
		queryParams.push(`environment_name=${import.meta.env.VITE_ENVIRONMENT_NAME}`);
		queryParams.push(`app_version=${import.meta.env.VITE_APP_VERSION}`);
		const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
		const url = `${this.baseUrl}/me${queryString}`;

		const response = await apiClient.get<IUser>(url, StandardAxiosConfig());
		const data = response.data;

		this._storeProgagricaTokens(data);

		return User.fromIUser(data, getLoginToken()!);
	}

	private _storeProgagricaTokens(data: IUser) {
		// TODO if we are going to store these here we should also use expiring cookies
		localStorage.setItem("proagrica_access_token", data.proagricaAuth?.access_token || "");
		localStorage.setItem("proagrica_refresh_token", data.proagricaAuth?.refresh_token || "");
		// localStorage.setItem('proagrica_auth', JSON.stringify(user.proagrica_auth));
	}

	public async getImpersonatableUsers(): Promise<ImpersonatableUser[]> {
		const url = `${this.baseUrl}/impersonate/users`;
		const response = await apiClient.get<ImpersonatableUser[]>(url, StandardAxiosConfig());
		return response.data;
	}

	public async updatePortalSettings(email: string, value: UserSettings) {
		const url = `${this.baseUrl}/update/portal-settings?email=${email}`;
		await apiClient.post(url, value, StandardAxiosConfig());
	}
}
